import {
  eventDescriptionLanguageValues,
  eventRoomVisibilityValues,
  eventTicketUrlKindValues,
  liveRoomModeValues,
} from '@pulsify/shared/dto'
import { z } from 'zod'
import * as m from '~/paraglide/messages'
import { richTextToPlainText } from '~/shared/lib/rich-text'
import { locationFormSchema, tagsInputSchema, tagsResponseSchema } from './shared-form-schemas'

export const eventLocationSchema = locationFormSchema

const richTextDescriptionSchema = z
  .string()
  .refine(
    (value) => richTextToPlainText(value).length >= 10,
    m.descriptionMustBeAtLeast10Characters(),
  )

const descriptionTranslationsSchema = z.object({
  de: z.string().optional(),
  en: z.string().optional(),
})

export const eventPriceSchema = z.discriminatedUnion('kind', [
  z.object({
    kind: z.literal('unknown'),
  }),
  z.object({
    kind: z.literal('free'),
  }),
  z.object({
    kind: z.literal('fixed'),
    priceFixed: z.number().min(0, m.priceMustBePositive()),
  }),
  z.object({
    kind: z.literal('range'),
    priceRangeMin: z.number().min(0, m.priceMustBePositive()),
    priceRangeMax: z.number().min(0, m.priceMustBePositive()),
  }),
])

const eventOccurrenceResponseSchema = z.object({
  id: z.string(),
  startTime: z.string(),
  endTime: z.string(),
  isAllDay: z.boolean().default(false),
})

/** Per-date occurrence as edited in the form: local `date` + `HH:MM` times. */
export const occurrenceFormSchema = z.object({
  date: z.string(),
  startTime: z.string(),
  endTime: z.string(),
  isAllDay: z.boolean().default(false),
})

export const eventSchema = z.object({
  id: z.string(),
  hostProfileId: z.string().nullable().optional(),
  title: z.string().min(1, m.eventTitleRequired()),
  description: richTextDescriptionSchema,
  descriptionLanguage: z.enum(eventDescriptionLanguageValues).default('de'),
  descriptionTranslations: descriptionTranslationsSchema.default({}),

  location: z.object({
    name: z.string().nullable(),
    street: z.string(),
    city: z.string(),
    country: z.string(),
    coordinates: z.object({
      lat: z.number(),
      lng: z.number(),
    }),
  }),
  locationPending: z.boolean().default(false),
  addressHidden: z.boolean().default(false),

  startTime: z.string(),
  endTime: z.string(),
  occurrences: z.array(eventOccurrenceResponseSchema).default([]),

  coverImageUrl: z.string().optional(),
  images: z
    .array(
      z.object({
        id: z.string(),
        url: z.string(),
        sortOrder: z.number().default(0),
      }),
    )
    .default([]),

  price: eventPriceSchema,

  ticketUrl: z.string().nullable().optional(),
  ticketUrlKind: z.enum(eventTicketUrlKindValues).default('tickets'),
  capacity: z.number().nullable().optional(),

  listingId: z.string().nullable(),

  status: z.enum(['Draft', 'Published', 'Unlisted', 'Importing']).optional(),
  isDraft: z.boolean().default(false),
  isCanceled: z.boolean().default(false),
  postponedOn: z.string().nullable().optional(),

  interestedCount: z.number().default(0),
  rsvpEnabled: z.boolean().default(false),
  rsvpCounts: z
    .object({
      going: z.number(),
      maybe: z.number(),
      interested: z.number().default(0),
      notGoing: z.number().default(0),
      plusOnes: z.number().default(0),
    })
    .optional(),
  liveRoomFunnel: z
    .object({
      scans: z.number(),
      joins: z.number(),
      guestJoins: z.number(),
      his: z.number(),
      mutuals: z.number(),
      accounts: z.number(),
    })
    .optional(),
  liveRoomMode: z.enum(liveRoomModeValues).default('open'),
  roomVisibility: z.enum(eventRoomVisibilityValues).nullable().default(null),
  requestEmails: z.boolean().default(true),
  plusOnesMax: z.number().default(0),
  minAge: z
    .union([z.literal(16), z.literal(18)])
    .nullable()
    .default(null),
  studentsOnly: z.boolean().default(false),
  membersOnly: z.boolean().default(false),
  audienceNote: z.string().nullable().default(null),
  targetAgeMin: z.number().nullable().default(null),
  targetAgeMax: z.number().nullable().default(null),

  tags: tagsResponseSchema,

  createdAt: z.string().optional(),
  updatedAt: z.string().optional(),
})

const createEventBaseSchema = z.object({
  title: z.string().min(1, m.eventTitleRequired()),
  description: richTextDescriptionSchema,
  descriptionLanguage: z.enum(eventDescriptionLanguageValues).optional(),
  descriptionTranslations: descriptionTranslationsSchema.optional(),
  location: eventLocationSchema,
  locationPending: z.boolean().optional(),
  addressHidden: z.boolean().optional(),
  startTime: z.string().min(1, m.startDateTimeRequired()),
  endTime: z.string().min(1, m.endDateTimeRequired()),
  occurrences: z.array(occurrenceFormSchema).default([]),
  price: eventPriceSchema,
  ticketUrl: z.string().pipe(z.url()).optional().or(z.literal('')),
  ticketUrlKind: z.enum(eventTicketUrlKindValues).optional(),
  capacity: z.number().positive().optional(),
  rsvpEnabled: z.boolean().optional(),
  liveRoomMode: z.enum(liveRoomModeValues).optional(),
  roomVisibility: z.enum(eventRoomVisibilityValues).nullable().optional(),
  requestEmails: z.boolean().optional(),
  plusOnesMax: z.number().int().min(0).optional(),
  minAge: z
    .union([z.literal(16), z.literal(18)])
    .nullable()
    .optional(),
  studentsOnly: z.boolean().optional(),
  membersOnly: z.boolean().optional(),
  audienceNote: z.string().optional(),
  targetAgeMin: z.number().int().optional(),
  targetAgeMax: z.number().int().optional(),
  tags: tagsInputSchema,
  listingId: z.string().nullable().optional(),
  hostProfileId: z.string().nullable().optional(),
})

export const createEventSchema = createEventBaseSchema.refine(
  (data) => new Date(data.endTime) > new Date(data.startTime),
  {
    message: m.endTimeMustBeAfterStartTime(),
    path: ['endTime'],
  },
)

export const updateEventSchema = createEventBaseSchema.partial().extend({
  id: z.string(),
})

export type Event = z.infer<typeof eventSchema>
export type EventPrice = z.infer<typeof eventPriceSchema>
export type OccurrenceForm = z.infer<typeof occurrenceFormSchema>
export type CreateEventInput = z.infer<typeof createEventSchema>
export type UpdateEventInput = z.infer<typeof updateEventSchema>
